<template>
	<div class="a11y-widget" role="complementary" :aria-label="t('accessibility.tools')">

		<!-- Floating toggle button -->
		<button
			class="a11y-toggle"
			:class="{ 'a11y-toggle--open': open }"
			@click="togglePanel"
			:aria-expanded="open ? 'true' : 'false'"
			aria-controls="a11y-panel"
			:aria-label="t('accessibility.openTools')"
		>
			<svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
				<circle cx="8" cy="2.6" r="1.6"/>
				<path d="M3.2 5.8c0-.44.36-.8.8-.8h8c.44 0 .8.36.8.8s-.36.8-.8.8H9.6v2l2.2 4.4a.8.8 0 1 1-1.43.72L8.8 10.4h-1.6l-1.57 3.32a.8.8 0 1 1-1.43-.72L6.4 8.6v-2H4c-.44 0-.8-.36-.8-.8z"/>
			</svg>
			<span class="a11y-sr-only">{{ t('accessibility.toolsTitle') }}</span>
		</button>

		<!-- Panel -->
		<div
			id="a11y-panel"
			class="a11y-panel"
			:class="{ 'a11y-panel--open': open }"
			role="dialog"
			aria-modal="false"
			:aria-label="t('accessibility.options')"
			v-show="open"
		>
			<!-- Header -->
			<div class="a11y-panel__header">
				<span class="a11y-panel__title">
					<svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style="vertical-align:-2px;margin-right:6px;">
						<circle cx="8" cy="2.6" r="1.6"/>
						<path d="M3.2 5.8c0-.44.36-.8.8-.8h8c.44 0 .8.36.8.8s-.36.8-.8.8H9.6v2l2.2 4.4a.8.8 0 1 1-1.43.72L8.8 10.4h-1.6l-1.57 3.32a.8.8 0 1 1-1.43-.72L6.4 8.6v-2H4c-.44 0-.8-.36-.8-.8z"/>
					</svg>
					Accessibility Tools
				</span>
				<button class="a11y-panel__close" @click="togglePanel" :aria-label="t('accessibility.closePanel')" :title="t('common.close')">
					<svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
						<path d="M18.3 5.7a1 1 0 0 0-1.4 0L12 10.6 7.1 5.7a1 1 0 0 0-1.4 1.4l4.9 4.9-4.9 4.9a1 1 0 1 0 1.4 1.4l4.9-4.9 4.9 4.9a1 1 0 0 0 1.4-1.4L13.4 12l4.9-4.9a1 1 0 0 0 0-1.4z"/>
					</svg>
				</button>
			</div>

			<!-- Content -->
			<div class="a11y-panel__body">

				<!-- Section: Text -->
				<div class="a11y-section">
					<div class="a11y-section__label">{{ t('accessibility.textSize') }}</div>
					<div class="a11y-row">
						<button class="a11y-btn" @click="decreaseFontSize" :disabled="fontSizeLevel <= 0" :aria-label="t('accessibility.decreaseTextSize')">
							<svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><text x="3" y="18" font-size="16" font-family="sans-serif">A</text><text x="14" y="20" font-size="11" font-family="sans-serif">-</text></svg>
							{{ t('accessibility.smaller') }}
						</button>
						<span class="a11y-level-indicator" aria-live="polite" aria-atomic="true">{{ t('accessibility.fontSizeLevels.' + fontSizeLabels[fontSizeLevel]) }}</span>
						<button class="a11y-btn" @click="increaseFontSize" :disabled="fontSizeLevel >= fontSizeSteps.length - 1" :aria-label="t('accessibility.increaseTextSize')">
							<svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><text x="2" y="18" font-size="16" font-family="sans-serif">A</text><text x="14" y="20" font-size="11" font-family="sans-serif">+</text></svg>
							{{ t('accessibility.larger') }}
						</button>
					</div>
				</div>

				<!-- Section: Visual -->
				<div class="a11y-section">
					<div class="a11y-section__label">{{ t('accessibility.visual') }}</div>
					<div class="a11y-grid">

						<button
							class="a11y-tile"
							:class="{ 'a11y-tile--active': highContrast }"
							@click="toggleHighContrast"
							:aria-pressed="highContrast ? 'true' : 'false'"
							:aria-label="t('accessibility.toggleHighContrast')"
						>
							<span class="a11y-tile__icon a11y-tile__icon--contrast" aria-hidden="true">◑</span>
							<span class="a11y-tile__text">{{ t('accessibility.highContrast') }}</span>
						</button>

						<button
							class="a11y-tile"
							:class="{ 'a11y-tile--active': invertColors }"
							@click="toggleInvertColors"
							:aria-pressed="invertColors ? 'true' : 'false'"
							:aria-label="t('accessibility.toggleInvertColors')"
						>
							<span class="a11y-tile__icon" aria-hidden="true" style="filter:invert(1);">☀</span>
							<span class="a11y-tile__text">{{ t('accessibility.invertColors') }}</span>
						</button>

						<button
							class="a11y-tile"
							:class="{ 'a11y-tile--active': grayscale }"
							@click="toggleGrayscale"
							:aria-pressed="grayscale ? 'true' : 'false'"
							:aria-label="t('accessibility.toggleGrayscale')"
						>
							<span class="a11y-tile__icon" aria-hidden="true" style="filter:grayscale(1);">🎨</span>
							<span class="a11y-tile__text">{{ t('accessibility.grayscale') }}</span>
						</button>

						<button
							class="a11y-tile"
							:class="{ 'a11y-tile--active': highlightLinks }"
							@click="toggleHighlightLinks"
							:aria-pressed="highlightLinks ? 'true' : 'false'"
							:aria-label="t('accessibility.toggleHighlightLinks')"
						>
							<span class="a11y-tile__icon" aria-hidden="true">🔗</span>
							<span class="a11y-tile__text">{{ t('accessibility.highlightLinks') }}</span>
						</button>

						<button
							class="a11y-tile"
							:class="{ 'a11y-tile--active': bigCursor }"
							@click="toggleBigCursor"
							:aria-pressed="bigCursor ? 'true' : 'false'"
							:aria-label="t('accessibility.toggleBigCursor')"
						>
							<span class="a11y-tile__icon" aria-hidden="true">🖱</span>
							<span class="a11y-tile__text">{{ t('accessibility.bigCursor') }}</span>
						</button>

						<button
							class="a11y-tile"
							:class="{ 'a11y-tile--active': focusHighlight }"
							@click="toggleFocusHighlight"
							:aria-pressed="focusHighlight ? 'true' : 'false'"
							:aria-label="t('accessibility.toggleFocusHighlight')"
						>
							<span class="a11y-tile__icon" aria-hidden="true">⌨</span>
							<span class="a11y-tile__text">{{ t('accessibility.focusHighlight') }}</span>
						</button>

					</div>
				</div>

				<!-- Section: Reading -->
				<div class="a11y-section">
					<div class="a11y-section__label">{{ t('accessibility.reading') }}</div>
					<div class="a11y-grid">

						<button
							class="a11y-tile"
							:class="{ 'a11y-tile--active': dyslexiaFont }"
							@click="toggleDyslexiaFont"
							:aria-pressed="dyslexiaFont ? 'true' : 'false'"
							:aria-label="t('accessibility.toggleDyslexiaFont')"
						>
							<span class="a11y-tile__icon" aria-hidden="true" style="font-family:Arial,sans-serif;font-weight:bold;font-size:1.2em;">Aa</span>
							<span class="a11y-tile__text">{{ t('accessibility.dyslexiaFont') }}</span>
						</button>

						<button
							class="a11y-tile"
							:class="{ 'a11y-tile--active': lineHeight }"
							@click="toggleLineHeight"
							:aria-pressed="lineHeight ? 'true' : 'false'"
							:aria-label="t('accessibility.toggleLineSpacing')"
						>
							<span class="a11y-tile__icon" aria-hidden="true" style="line-height:1;font-size:1.1em;">≡</span>
							<span class="a11y-tile__text">{{ t('accessibility.lineSpacing') }}</span>
						</button>

						<button
							class="a11y-tile"
							:class="{ 'a11y-tile--active': textSpacing }"
							@click="toggleTextSpacing"
							:aria-pressed="textSpacing ? 'true' : 'false'"
							:aria-label="t('accessibility.toggleTextSpacing')"
						>
							<span class="a11y-tile__icon" aria-hidden="true" style="letter-spacing:3px;font-size:0.9em;">A B</span>
							<span class="a11y-tile__text">{{ t('accessibility.textSpacing') }}</span>
						</button>

						<button
							class="a11y-tile"
							:class="{ 'a11y-tile--active': pauseAnimations }"
							@click="togglePauseAnimations"
							:aria-pressed="pauseAnimations ? 'true' : 'false'"
							:aria-label="t('accessibility.togglePauseAnimations')"
						>
							<span class="a11y-tile__icon" aria-hidden="true">⏸</span>
							<span class="a11y-tile__text">{{ t('accessibility.pauseAnimations') }}</span>
						</button>

						<button
							class="a11y-tile"
							:class="{ 'a11y-tile--active': readingGuide }"
							@click="toggleReadingGuide"
							:aria-pressed="readingGuide ? 'true' : 'false'"
							:aria-label="t('accessibility.toggleReadingGuide')"
						>
							<span class="a11y-tile__icon" aria-hidden="true">📖</span>
							<span class="a11y-tile__text">{{ t('accessibility.readingGuide') }}</span>
						</button>

						<button
							class="a11y-tile"
							:class="{ 'a11y-tile--active': hideImages }"
							@click="toggleHideImages"
							:aria-pressed="hideImages ? 'true' : 'false'"
							:aria-label="t('accessibility.toggleHideImages')"
						>
							<span class="a11y-tile__icon" aria-hidden="true">🖼</span>
							<span class="a11y-tile__text">{{ t('accessibility.hideImages') }}</span>
						</button>

					</div>
				</div>

				<!-- Reset -->
				<div class="a11y-section" style="border-top:1px solid #e5e7eb;padding-top:10px;margin-top:4px;">
					<button class="a11y-reset-btn" @click="resetAll" :aria-label="t('accessibility.resetAria')">
						↺ {{ t('accessibility.resetAll') }}
					</button>
				</div>

			</div>

			<!-- Footer note -->
			<div class="a11y-panel__footer">
				{{ t('accessibility.savedNote') }}
			</div>
		</div>

		<!-- Reading guide ruler (follows mouse) -->
		<div
			v-if="readingGuide"
			class="a11y-reading-guide"
			:style="{ top: guideY + 'px' }"
			aria-hidden="true"
		></div>

	</div>
</template>

<script>
import { t } from '@/websites/i18n/websiteI18n';
/**
 * AccessibilityWidget.vue
 * ───────────────────────
 * A floating, keyboard-accessible widget that provides on-the-fly assistive
 * technology adjustments for the public website.
 *
 * Features:
 *  - Text size adjustment (5 levels)
 *  - High contrast mode
 *  - Inverted colours
 *  - Grayscale mode
 *  - Link highlighting
 *  - Large cursor
 *  - Focus highlight ring
 *  - Dyslexia-friendly font (Arial wide-spacing approximation)
 *  - Increased line height
 *  - Increased letter/word spacing
 *  - Pause CSS animations
 *  - Reading guide ruler
 *  - Hide images (reduces visual noise)
 *
 * All preferences are persisted in localStorage under the key
 * "a11y-prefs" so they are restored on the next page load.
 *
 * CSS classes are applied to <html> so they cascade across the entire
 * document including content outside .site-root.
 */

var STORAGE_KEY = 'a11y-prefs';

var FONT_SIZE_STEPS = [0.85, 1, 1.15, 1.3, 1.5];
var FONT_SIZE_LABELS = ['smallest', 'default', 'medium', 'large', 'largest'];

export default {
	name: 'AccessibilityWidget',

	props: {
		/** Set to true inside the CMS preview iframe to hide the widget */
		hidden: {
			type: Boolean,
			default: false
		}
	},

	data: function () {
		return {
			open: false,

			// Text size
			fontSizeLevel: 1,
			fontSizeSteps: FONT_SIZE_STEPS,
			fontSizeLabels: FONT_SIZE_LABELS,

			// Toggles
			highContrast:     false,
			invertColors:     false,
			grayscale:        false,
			highlightLinks:   false,
			bigCursor:        false,
			focusHighlight:   false,
			dyslexiaFont:     false,
			lineHeight:       false,
			textSpacing:      false,
			pauseAnimations:  false,
			readingGuide:     false,
			hideImages:       false,

			// Reading guide position
			guideY: 200
		};
	},

	mounted: function () {
		this.loadPrefs();
		this.applyAll();

		// Bind listeners with stable references so they can be removed later
		var self = this;
		this._onMouseMove = function (e) {
			self.guideY = e.clientY - 12;
		};
		this._onKeyDown = function (e) {
			if (e.key === 'Escape' && self.open) {
				self.open = false;
			}
		};

		// Close panel on Escape — always active (lightweight)
		document.addEventListener('keydown', this._onKeyDown);

		// Reading guide mouse tracking — start only if preference was restored
		if (this.readingGuide) {
			document.addEventListener('mousemove', this._onMouseMove);
		}
	},

	beforeDestroy: function () {
		this.removeAll();
		document.removeEventListener('keydown', this._onKeyDown);
		document.removeEventListener('mousemove', this._onMouseMove);
	},

	methods: {
		t,

		togglePanel: function () {
			this.open = !this.open;
		},

		// ── Font size ──────────────────────────────────────────────────────────
		increaseFontSize: function () {
			if (this.fontSizeLevel < this.fontSizeSteps.length - 1) {
				this.fontSizeLevel++;
				this.applyFontSize();
				this.savePrefs();
			}
		},

		decreaseFontSize: function () {
			if (this.fontSizeLevel > 0) {
				this.fontSizeLevel--;
				this.applyFontSize();
				this.savePrefs();
			}
		},

		applyFontSize: function () {
			var root = document.documentElement;
			var scale = this.fontSizeSteps[this.fontSizeLevel];
			if (scale === 1) {
				root.style.removeProperty('font-size');
			} else {
				root.style.fontSize = (scale * 100) + '%';
			}
		},

		// ── Toggle helpers ─────────────────────────────────────────────────────
		toggleHighContrast: function () {
			this.highContrast = !this.highContrast;
			this.toggle('a11y-high-contrast', this.highContrast);
			this.savePrefs();
		},

		toggleInvertColors: function () {
			this.invertColors = !this.invertColors;
			this.toggle('a11y-invert', this.invertColors);
			this.savePrefs();
		},

		toggleGrayscale: function () {
			this.grayscale = !this.grayscale;
			this.toggle('a11y-grayscale', this.grayscale);
			this.savePrefs();
		},

		toggleHighlightLinks: function () {
			this.highlightLinks = !this.highlightLinks;
			this.toggle('a11y-highlight-links', this.highlightLinks);
			this.savePrefs();
		},

		toggleBigCursor: function () {
			this.bigCursor = !this.bigCursor;
			this.toggle('a11y-big-cursor', this.bigCursor);
			this.savePrefs();
		},

		toggleFocusHighlight: function () {
			this.focusHighlight = !this.focusHighlight;
			this.toggle('a11y-focus-highlight', this.focusHighlight);
			this.savePrefs();
		},

		toggleDyslexiaFont: function () {
			this.dyslexiaFont = !this.dyslexiaFont;
			this.toggle('a11y-dyslexia', this.dyslexiaFont);
			this.savePrefs();
		},

		toggleLineHeight: function () {
			this.lineHeight = !this.lineHeight;
			this.toggle('a11y-line-height', this.lineHeight);
			this.savePrefs();
		},

		toggleTextSpacing: function () {
			this.textSpacing = !this.textSpacing;
			this.toggle('a11y-text-spacing', this.textSpacing);
			this.savePrefs();
		},

		togglePauseAnimations: function () {
			this.pauseAnimations = !this.pauseAnimations;
			this.toggle('a11y-pause-animations', this.pauseAnimations);
			this.savePrefs();
		},

		toggleReadingGuide: function () {
			this.readingGuide = !this.readingGuide;
			if (this.readingGuide) {
				document.addEventListener('mousemove', this._onMouseMove);
			} else {
				document.removeEventListener('mousemove', this._onMouseMove);
			}
			this.savePrefs();
		},

		toggleHideImages: function () {
			this.hideImages = !this.hideImages;
			this.toggle('a11y-hide-images', this.hideImages);
			this.savePrefs();
		},

		// ── Reset ──────────────────────────────────────────────────────────────
		resetAll: function () {
			this.fontSizeLevel    = 1;
			this.highContrast     = false;
			this.invertColors     = false;
			this.grayscale        = false;
			this.highlightLinks   = false;
			this.bigCursor        = false;
			this.focusHighlight   = false;
			this.dyslexiaFont     = false;
			this.lineHeight       = false;
			this.textSpacing      = false;
			this.pauseAnimations  = false;
			this.readingGuide     = false;
			this.hideImages       = false;
			this.removeAll();
			try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* */ }
		},

		// ── Helpers ────────────────────────────────────────────────────────────

		/** Add or remove a CSS class on <html> */
		toggle: function (cls, state) {
			var root = document.documentElement;
			if (state) {
				root.classList.add(cls);
			} else {
				root.classList.remove(cls);
			}
		},

		/** Apply all current preferences to the document */
		applyAll: function () {
			this.applyFontSize();
			this.toggle('a11y-high-contrast',    this.highContrast);
			this.toggle('a11y-invert',           this.invertColors);
			this.toggle('a11y-grayscale',        this.grayscale);
			this.toggle('a11y-highlight-links',  this.highlightLinks);
			this.toggle('a11y-big-cursor',       this.bigCursor);
			this.toggle('a11y-focus-highlight',  this.focusHighlight);
			this.toggle('a11y-dyslexia',         this.dyslexiaFont);
			this.toggle('a11y-line-height',      this.lineHeight);
			this.toggle('a11y-text-spacing',     this.textSpacing);
			this.toggle('a11y-pause-animations', this.pauseAnimations);
			this.toggle('a11y-hide-images',      this.hideImages);
		},

		/** Remove every a11y class and reset font size */
		removeAll: function () {
			var classes = [
				'a11y-high-contrast', 'a11y-invert', 'a11y-grayscale',
				'a11y-highlight-links', 'a11y-big-cursor', 'a11y-focus-highlight',
				'a11y-dyslexia', 'a11y-line-height', 'a11y-text-spacing',
				'a11y-pause-animations', 'a11y-hide-images'
			];
			var root = document.documentElement;
			classes.forEach(function (c) { root.classList.remove(c); });
			root.style.removeProperty('font-size');
		},

		// ── Persistence ────────────────────────────────────────────────────────

		savePrefs: function () {
			try {
				var prefs = {
					fontSizeLevel:   this.fontSizeLevel,
					highContrast:    this.highContrast,
					invertColors:    this.invertColors,
					grayscale:       this.grayscale,
					highlightLinks:  this.highlightLinks,
					bigCursor:       this.bigCursor,
					focusHighlight:  this.focusHighlight,
					dyslexiaFont:    this.dyslexiaFont,
					lineHeight:      this.lineHeight,
					textSpacing:     this.textSpacing,
					pauseAnimations: this.pauseAnimations,
					readingGuide:    this.readingGuide,
					hideImages:      this.hideImages
				};
				localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
			} catch (e) { /* localStorage unavailable — no-op */ }
		},

		loadPrefs: function () {
			try {
				var raw = localStorage.getItem(STORAGE_KEY);
				if (!raw) { return; }
				var prefs = JSON.parse(raw);

				if (typeof prefs.fontSizeLevel === 'number' &&
					prefs.fontSizeLevel >= 0 &&
					prefs.fontSizeLevel < FONT_SIZE_STEPS.length) {
					this.fontSizeLevel = prefs.fontSizeLevel;
				}
				if (prefs.highContrast)    { this.highContrast    = true; }
				if (prefs.invertColors)    { this.invertColors    = true; }
				if (prefs.grayscale)       { this.grayscale       = true; }
				if (prefs.highlightLinks)  { this.highlightLinks  = true; }
				if (prefs.bigCursor)       { this.bigCursor       = true; }
				if (prefs.focusHighlight)  { this.focusHighlight  = true; }
				if (prefs.dyslexiaFont)    { this.dyslexiaFont    = true; }
				if (prefs.lineHeight)      { this.lineHeight      = true; }
				if (prefs.textSpacing)     { this.textSpacing     = true; }
				if (prefs.pauseAnimations) { this.pauseAnimations = true; }
				if (prefs.readingGuide)    { this.readingGuide    = true; }
				if (prefs.hideImages)      { this.hideImages      = true; }
			} catch (e) { /* parse error — use defaults */ }
		}
	}
};
</script>

<style>
/* ─────────────────────────────────────────────────────────────────────────────
   Accessibility class overrides — applied to <html>
   These must be in a non-scoped block so they cascade to the full page.
───────────────────────────────────────────────────────────────────────────── */

/* High contrast */
html.a11y-high-contrast,
html.a11y-high-contrast body {
	background-color: #000000 !important;
	color: #FFFFFF !important;
}
html.a11y-high-contrast a {
	color: #FFFF00 !important;
	text-decoration: underline !important;
}
html.a11y-high-contrast img,
html.a11y-high-contrast video {
	filter: contrast(1.4) brightness(1.1);
}
html.a11y-high-contrast button,
html.a11y-high-contrast input,
html.a11y-high-contrast select,
html.a11y-high-contrast textarea {
	background-color: #222 !important;
	color: #FFFFFF !important;
	border-color: #FFFFFF !important;
}

/* Invert */
html.a11y-invert {
	filter: invert(1) hue-rotate(180deg);
}
html.a11y-invert img,
html.a11y-invert video,
html.a11y-invert iframe {
	filter: invert(1) hue-rotate(180deg);
}

/* Grayscale */
html.a11y-grayscale {
	filter: grayscale(1);
}
html.a11y-grayscale img,
html.a11y-grayscale video {
	filter: grayscale(1);
}

/* Highlight links */
html.a11y-highlight-links a {
	outline: 2px solid #FF6600 !important;
	outline-offset: 2px;
	text-decoration: underline !important;
	text-underline-offset: 3px;
}

/* Big cursor */
html.a11y-big-cursor,
html.a11y-big-cursor * {
	cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 48 48'%3E%3Cpath d='M8 4l32 20-14 2-8 14z' fill='%23000' stroke='%23fff' stroke-width='2'/%3E%3C/svg%3E") 4 4, auto !important;
}

/* Focus highlight */
html.a11y-focus-highlight *:focus {
	outline: 3px solid #FF6600 !important;
	outline-offset: 3px !important;
	box-shadow: 0 0 0 5px rgba(255, 102, 0, 0.3) !important;
}

/* Dyslexia font — readable, spaced */
html.a11y-dyslexia,
html.a11y-dyslexia body,
html.a11y-dyslexia p,
html.a11y-dyslexia li,
html.a11y-dyslexia span,
html.a11y-dyslexia div,
html.a11y-dyslexia h1,
html.a11y-dyslexia h2,
html.a11y-dyslexia h3,
html.a11y-dyslexia h4,
html.a11y-dyslexia h5,
html.a11y-dyslexia h6 {
	font-family: Arial, Helvetica, sans-serif !important;
	word-spacing: 0.2em !important;
	letter-spacing: 0.05em !important;
}

/* Increased line height */
html.a11y-line-height,
html.a11y-line-height body,
html.a11y-line-height p,
html.a11y-line-height li,
html.a11y-line-height span,
html.a11y-line-height div {
	line-height: 2 !important;
}

/* Increased text spacing */
html.a11y-text-spacing,
html.a11y-text-spacing body,
html.a11y-text-spacing p,
html.a11y-text-spacing li,
html.a11y-text-spacing span,
html.a11y-text-spacing div {
	letter-spacing: 0.12em !important;
	word-spacing: 0.25em !important;
}

/* Pause animations */
html.a11y-pause-animations *,
html.a11y-pause-animations *::before,
html.a11y-pause-animations *::after {
	animation-play-state: paused !important;
	transition: none !important;
}

/* Hide images */
html.a11y-hide-images img,
html.a11y-hide-images figure,
html.a11y-hide-images video,
html.a11y-hide-images [role="img"] {
	visibility: hidden !important;
}
</style>

<style scoped>
/* ─────────────────────────────────────────────────────────────────────────────
   Widget chrome — scoped to the component
───────────────────────────────────────────────────────────────────────────── */

/* Screen-reader only text */
.a11y-sr-only {
	position: absolute !important;
	width: 1px !important;
	height: 1px !important;
	padding: 0 !important;
	margin: -1px !important;
	overflow: hidden !important;
	clip: rect(0,0,0,0) !important;
	white-space: nowrap !important;
	border: 0 !important;
}

/* ── Floating button ── */
.a11y-toggle {
	position: fixed;
	bottom: 20px;
	right: 20px;
	z-index: 99990;
	width: 40px;
	height: 40px;
	border-radius: 50%;
	border: none;
	cursor: pointer;
	background: #1E2031;
	color: #FFFFFF;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 2px 10px rgba(0,0,0,0.3);
	transition: background 0.2s, transform 0.2s;
	padding: 0;
	outline: none;
}
.a11y-toggle:hover {
	background: #FB6A00;
	transform: scale(1.08);
}
.a11y-toggle:focus-visible {
	outline: 3px solid #FB6A00;
	outline-offset: 3px;
}
.a11y-toggle--open {
	background: #FB6A00;
}

/* ── Panel ── */
.a11y-panel {
	position: fixed;
	bottom: 68px;
	right: 20px;
	z-index: 99989;
	width: 320px;
	max-height: 80vh;
	background: #FFFFFF;
	border-radius: 12px;
	box-shadow: 0 8px 32px rgba(0,0,0,0.22), 0 1px 4px rgba(0,0,0,0.1);
	display: flex;
	flex-direction: column;
	overflow: hidden;
	transition: transform 0.22s ease, opacity 0.22s ease;
	transform-origin: bottom right;
}

/* ── Panel header ── */
.a11y-panel__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 14px 16px 12px;
	background: #1E2031;
	color: #FFFFFF;
	flex-shrink: 0;
}
.a11y-panel__title {
	font-size: 0.95em;
	font-weight: 700;
	letter-spacing: 0.02em;
	font-family: 'Open Sans', Arial, sans-serif;
}
.a11y-panel__close {
	background: transparent;
	border: none;
	color: #FFFFFF;
	cursor: pointer;
	padding: 4px;
	border-radius: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background 0.15s;
	line-height: 1;
}
.a11y-panel__close:hover {
	background: rgba(255,255,255,0.15);
}
.a11y-panel__close:focus-visible {
	outline: 2px solid #FB6A00;
	outline-offset: 2px;
}

/* ── Panel body ── */
.a11y-panel__body {
	overflow-y: auto;
	flex: 1 1 auto;
	padding: 8px 14px 4px;
	-webkit-overflow-scrolling: touch;
}

/* ── Section ── */
.a11y-section {
	margin-bottom: 10px;
}
.a11y-section__label {
	font-size: 0.72em;
	font-weight: 700;
	letter-spacing: 0.1em;
	text-transform: uppercase;
	color: #9ca3af;
	margin-bottom: 6px;
	padding-left: 2px;
}

/* ── Text size row ── */
.a11y-row {
	display: flex;
	align-items: center;
	gap: 8px;
}
.a11y-level-indicator {
	flex: 1;
	text-align: center;
	font-size: 0.82em;
	color: #6b7280;
	font-weight: 600;
}
.a11y-btn {
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 2px;
	padding: 7px 4px;
	background: #f3f4f6;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	cursor: pointer;
	font-size: 0.75em;
	color: #374151;
	transition: background 0.15s, border-color 0.15s;
	line-height: 1.2;
}
.a11y-btn:hover:not(:disabled) {
	background: #e9ecff;
	border-color: #FB6A00;
	color: #FB6A00;
}
.a11y-btn:disabled {
	opacity: 0.4;
	cursor: default;
}
.a11y-btn:focus-visible {
	outline: 2px solid #FB6A00;
	outline-offset: 2px;
}

/* ── Grid of tiles ── */
.a11y-grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 6px;
}
.a11y-tile {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: flex-start;
	gap: 4px;
	padding: 8px 4px 7px;
	background: #f3f4f6;
	border: 1.5px solid #e5e7eb;
	border-radius: 8px;
	cursor: pointer;
	font-size: 0.72em;
	color: #374151;
	line-height: 1.2;
	text-align: center;
	transition: background 0.15s, border-color 0.15s, color 0.15s;
	min-height: 56px;
}
.a11y-tile:hover {
	background: #fff4ec;
	border-color: #FB6A00;
	color: #FB6A00;
}
.a11y-tile--active {
	background: #1E2031 !important;
	border-color: #1E2031 !important;
	color: #FFFFFF !important;
}
.a11y-tile--active .a11y-tile__icon {
	filter: none;
}
.a11y-tile:focus-visible {
	outline: 2px solid #FB6A00;
	outline-offset: 2px;
}
.a11y-tile__icon {
	font-size: 1.3em;
	line-height: 1;
	display: block;
}
.a11y-tile__text {
	display: block;
	font-size: 0.95em;
}

/* ── Reset button ── */
.a11y-reset-btn {
	width: 100%;
	padding: 9px 12px;
	background: transparent;
	border: 1.5px solid #e5e7eb;
	border-radius: 7px;
	color: #6b7280;
	font-size: 0.82em;
	font-weight: 600;
	cursor: pointer;
	transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.a11y-reset-btn:hover {
	background: #fee2e2;
	color: #E31515;
	border-color: #E31515;
}
.a11y-reset-btn:focus-visible {
	outline: 2px solid #E31515;
	outline-offset: 2px;
}

/* ── Footer ── */
.a11y-panel__footer {
	padding: 8px 16px 10px;
	font-size: 0.7em;
	color: #9ca3af;
	text-align: center;
	flex-shrink: 0;
	border-top: 1px solid #f3f4f6;
}

/* ── Reading guide ── */
.a11y-reading-guide {
	position: fixed;
	left: 0;
	right: 0;
	height: 24px;
	background: rgba(255, 255, 0, 0.25);
	pointer-events: none;
	z-index: 99988;
	border-top: 1px solid rgba(255,200,0,0.5);
	border-bottom: 1px solid rgba(255,200,0,0.5);
}

/* ── Responsive: move to left side on narrow screens ── */
@media (max-width: 480px) {
	.a11y-toggle {
		bottom: 14px;
		right: 14px;
		width: 36px;
		height: 36px;
	}
	.a11y-panel {
		right: 8px;
		left: 8px;
		width: auto;
		bottom: 76px;
	}
}
</style>
